import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from 'util/UserContext';
import { useLocation } from 'react-router-dom';
import Modal from 'ui/Modal';
import { Drawer, DrawerContent } from 'ui/Drawer';
import api from 'config/axiosConfig';
import EntityCompactCard from 'components/Entities/EntityCompactCard';
import IssueSearch from 'components/Issues/IssueSearch';
import AddButton from 'ui/AddButton';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { Issue, Suggestion } from 'util/constants';
import { createIssueFromWikipedia, createCompoundIssue } from 'util/issueCreationUtils';
import { Info } from 'lucide-react';
import { toast } from "sonner";
import EmptyState from 'ui/EmptyState';
import { apiRoutes } from "util/apiRoutes";

interface Props {
  issueId: number;
  onClose: () => void;
  initialTab?: 'influenced' | 'influencer';
  displayMode?: 'all' | 'influencers' | 'influenced'; // 'all' shows both tabs, others show single tab
}

const InfluencesModal: React.FC<Props> = ({ issueId, onClose, initialTab = 'influenced', displayMode = 'all' }) => {
  const { userIsMobile, currentUser, setShowAuthModal } = useUser();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Determine the actual initial tab based on display mode
  const getInitialTab = () => {
    if (displayMode === 'influencers') return 'influencer';
    if (displayMode === 'influenced') return 'influenced';
    return initialTab;
  };
  
  const [selectedTab, setSelectedTab] = useState<string>(getInitialTab());
  const [influencerIssues, setInfluencerIssues] = useState<Issue[]>([]);
  const [influencedIssues, setInfluencedIssues] = useState<Issue[]>([]);
  const [influencerTotal, setInfluencerTotal] = useState(0);
  const [influencedTotal, setInfluencedTotal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Suggestion | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [currentIssueData, setCurrentIssueData] = useState<Issue | null>(null);

  // Store initial pathname to detect navigation
  const [initialPathname] = useState(location.pathname);
  
  // Close modal when location changes (user navigates to another page)
  useEffect(() => {
    if (location.pathname !== initialPathname) {
      onClose();
    }
  }, [location.pathname, initialPathname, onClose]);

  const fetchInfluences = useCallback(async () => {
    try {
      setLoading(true);
      const [influencerResponse, influencedResponse, currentIssueResponse] = await Promise.all([
        api.get(apiRoutes.issues.influences(issueId), {
          params: { type: 'influencer', limit: 50 }
        }),
        api.get(apiRoutes.issues.influences(issueId), {
          params: { type: 'influenced', limit: 50 }
        }),
        api.get(apiRoutes.issues.byId(issueId))
      ]);
      
      const influencerData = influencerResponse.data.influences || [];
      const influencedData = influencedResponse.data.influences || [];
      
      setInfluencerIssues(influencerData);
      setInfluencerTotal(influencerResponse.data.total || 0);
      
      setInfluencedIssues(influencedData);
      setInfluencedTotal(influencedResponse.data.total || 0);
      
      setCurrentIssueData(currentIssueResponse.data);
    } catch (error) {
      console.error('Error fetching influences:', error);
      setError('Failed to load influences');
    } finally {
      setLoading(false);
    }
  }, [issueId]);

  useEffect(() => {
    fetchInfluences();
  }, [fetchInfluences]);

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    setSelectedIssue(null);
    setActionMessage(null);
  };


  const handleIssueSelect = (suggestion: Suggestion) => {
    setSelectedIssue(suggestion);
    setActionMessage(null);
  };

  const handleAddInfluence = async () => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    if (!selectedIssue || !currentIssueData) {
      setActionMessage("Please select an issue first");
      return;
    }

    setIsSubmitting(true);
    setActionMessage(null);
    
    try {
      let selectedIssueToLink = selectedIssue;
      const currentIssue = currentIssueData;

      // If it's a wiki suggestion, create the issue first
      if (selectedIssue.type === 'wiki') {
        const createdIssue = await createIssueFromWikipedia(selectedIssue);
        selectedIssueToLink = {...createdIssue, type: 'issue' as const};
      }

      // Helper function to get Wikipedia link from an issue/suggestion
      const getWikipediaLink = (issue: Issue | Suggestion): string => {
        const properties = (issue as Issue).properties;
        if (properties?.is_custom_issue || properties?.subject_properties?.is_custom_issue) {
          return "";
        }
        // Try direct wikipedia_link property first
        if ("wikipedia_link" in issue && issue.wikipedia_link) return issue.wikipedia_link;
        // Try topic_wikipedia_link for compound issues
        if ("topic_wikipedia_link" in issue && issue.topic_wikipedia_link) return issue.topic_wikipedia_link;
        // Construct from cname if available
        if ("cname" in issue && issue.cname) {
          const title = issue.cname.replace(/_/g, " ");
          return `https://en.wikipedia.org/wiki/${encodeURIComponent(title)}`;
        }
        return "";
      };

      // Create the compound issue (relationship)
      if (selectedTab === 'influencer') {
        // Selected issue is the influencer (subject), current issue is influenced (topic)
        await createCompoundIssue(
          selectedIssueToLink.cname || '',
          currentIssue.cname,
          getWikipediaLink(selectedIssueToLink),
          getWikipediaLink(currentIssue),
          (selectedIssueToLink as any).properties?.subject_properties,
          currentIssue.properties?.subject_properties
        );
      } else {
        // Current issue is the influencer (subject), selected issue is influenced (topic)
        await createCompoundIssue(
          currentIssue.cname,
          selectedIssueToLink.cname || '',
          getWikipediaLink(currentIssue),
          getWikipediaLink(selectedIssueToLink),
          currentIssue.properties?.subject_properties,
          (selectedIssueToLink as any).properties?.subject_properties
        );
      }
      
      // Refresh the influences lists
      await fetchInfluences();
      
      // Reset selection after adding
      setSelectedIssue(null);
      
      // Show success toast notification instead of inline message
      toast.success("Influence relationship added successfully");
    } catch (error: any) {
      console.error('Error adding influence relationship:', error);
      setActionMessage('Couldn\'t create the influence relationship. Make sure this isn\'t already an influence');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getExcludedIssueIds = () => {
    const currentList = selectedTab === 'influencer' 
      ? influencerIssues 
      : influencedIssues;
    
    return currentList.map(influence => influence.id).filter(id => id !== undefined);
  };

  // Helper function to create a displayable Issue object from influence data
  // Helper function to determine which tabs to show based on display mode
  const getVisibleTabs = () => {
    const allTabs = [
      { 
        label: `${influencerTotal} Influencers`, 
        value: 'influencer'
      },
      { 
        label: `${influencedTotal} Influenced`, 
        value: 'influenced'
      }
    ];

    // Filter tabs based on display mode - completely hide irrelevant tabs
    switch (displayMode) {
      case 'influencers':
        return allTabs.filter(tab => tab.value === 'influencer');
      case 'influenced':
        return allTabs.filter(tab => tab.value === 'influenced');
      case 'all':
      default:
        return allTabs;
    }
  };

  const createDisplayEntity = (influence: Issue, isInfluencer: boolean): Issue => {
    // Ensure influence object exists
    if (!influence) {
      console.error('Influence object is undefined');
      return {
        id: 0,
        name: 'Error',
        cname: 'Error',
        properties: { subject_properties: {} },
        wikipedia_link: '',
        username: '',
        properties_public: {},
        created_at: '',
        updated_at: '',
        vote_count: 0
      } as Issue;
    }

    if (isInfluencer) {
      // For influencers tab, show the subject (influencer)
      const name = influence.subject_cname.replace(/_/g, ' ') || 'Unknown Influencer';
      return {
        id: influence.id || 0,
        name: name,
        cname: influence.cname,
        properties: {
          subject_properties: influence.properties?.subject_properties || {}
        },
        wikipedia_link: influence.wikipedia_link || '',
        username: influence.username || '',
        properties_public: influence.properties_public || {},
        created_at: influence.created_at || '',
        updated_at: influence.updated_at || '',
        vote_count: influence.vote_count || 0
      } as Issue;
    } else {
      // For influenced tab, show the topic (influenced)
      const name = influence.topic_cname.replace(/_/g, ' ') || 'Unknown Influenced';
      return {
        id: influence.id || 0,
        name: name,
        cname: influence.cname,
        properties: {
          subject_properties: influence.properties?.topic_properties || {}
        },
        wikipedia_link: influence.topic_wikipedia_link || '',
        username: influence.username || '',
        properties_public: influence.properties_public || {},
        created_at: influence.created_at || '',
        updated_at: influence.updated_at || '',
        vote_count: influence.vote_count || 0
      } as Issue;
    }
  };

  const visibleTabs = getVisibleTabs();
  
  // Custom tabs component
  const CustomTabsList = () => (
    <div className="flex space-x-4 overflow-x-auto whitespace-nowrap scrollbar-hide w-full">
      {visibleTabs.map((tabItem) => (
        <div
          key={tabItem.value}
          className={`
            flex-shrink-0 cursor-pointer select-none transition-colors
            ${selectedTab === tabItem.value
              ? 'tab-active' 
              : 'tab-inactive hover:text-eoe-text-heading dark:hover:text-eoe-dark-text-heading'
            }
          `}
          onClick={() => handleTabChange(tabItem.value)}
        >
          {tabItem.label}
        </div>
      ))}
    </div>
  );
  
  const header = (
    <div className="flex-shrink-0 space-y-4 mb-4">
      {visibleTabs.length > 0 && <CustomTabsList />}
    </div>
  );

  const footer = (
    <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:justify-end sm:gap-x-2 py-2">
      <div>
        <IssueSearch
          inputType="add"
          placeholder={`Search ${selectedTab === 'influencer' ? 'influencers' : 'influenced'}`}
          excludeIssueIds={[issueId, ...getExcludedIssueIds()]}
          excludeCompoundIssues={true}
          onSelect={handleIssueSelect}
          className="w-full"
          prepopulateData={selectedIssue as any}
        />
      </div>
      <AddButton
        tooltipTitle={`Add ${selectedTab === 'influencer' ? 'influencer' : 'influenced'}`}
        buttonText={selectedTab === 'influencer' ? 'Influencer' : 'Influenced'}
        onClick={handleAddInfluence}
        className={`w-full ${isSubmitting || !selectedIssue ? 'opacity-50 cursor-not-allowed' : ''}`}
      />
      
      {actionMessage && (
        <div className={`text-sm flex items-center ${actionMessage.includes('success') ? 'text-green-600 dark:text-green-400' : 'text-warning-red dark:text-warning-red-dark'}`}>
          <Info className="mr-1 size-4" />
          <span>{actionMessage}</span>
        </div>
      )}
    </div>
  );

  const content = (
    <>
      {loading ? (
        <LoadingSkeleton variant="entity-avatar" avatarSize="h-9 w-9" count={10} margin="mx-2"/>
      ) : error ? (
        <div className="text-warning-red dark:text-warning-red-dark text-center p-4">
          {error}
        </div>
      ) : selectedTab === 'influencer' && influencerIssues.length === 0 ? (
        <EmptyState 
          title="No influencers yet"
          subtitle="Be the first to add an issue that influences this one"
          className="p-1"
        />
      ) : selectedTab === 'influenced' && influencedIssues.length === 0 ? (
        <EmptyState 
          title="No influenced yet"
          subtitle="Be the first to add an issue that is influenced by this one"
          className="p-1"
        />
      ) : (
        <div className="space-y-2 p-1">
          {selectedTab === 'influencer' 
            ? influencerIssues.map(issue => (
                <EntityCompactCard
                  key={issue.id}
                  entity={issue}
                  displayEntity={createDisplayEntity(issue, true)}
                  entityType="issue"
                  showMeta={true}
                  showActions={true}
                />
              ))
            : influencedIssues.map(issue => (
                <EntityCompactCard
                  key={issue.id}
                  entity={issue}
                  displayEntity={createDisplayEntity(issue, false)}
                  entityType="issue"
                  showMeta={true}
                  showActions={true}
                />
              ))
          }
        </div>
      )}
    </>
  );

  return (
    <>
      {userIsMobile ? (
        <Drawer open={true} onOpenChange={onClose}>
          <DrawerContent header={header} footer={footer} stickyFooter={true}>
            {content}
          </DrawerContent>
        </Drawer>
      ) : (
        <Modal 
          isOpen={true} 
          onClose={onClose} 
          title={displayMode === 'influencers' ? 'Influencers' : displayMode === 'influenced' ? 'Influenced' : 'Influences'}
        >
          <div className="p-1 flex flex-col w-full h-[600px] max-h-[80vh] overflow-hidden">
            {visibleTabs.length > 0 && (
              <div className="flex-shrink-0 mb-2">
                <CustomTabsList />
              </div>
            )}
            
            <div className="flex-1 overflow-y-auto mb-4">
              {content}
            </div>
            
            <div className="flex-shrink-0 space-y-2 p-1">
              <div>
                <IssueSearch
                  inputType="add"
                  placeholder={`Search ${selectedTab === 'influencer' ? 'influencers' : 'influenced'}`}
                  excludeIssueIds={[issueId, ...getExcludedIssueIds()]}
                  excludeCompoundIssues={true}
                  onSelect={handleIssueSelect}
                  className="w-full"
                  prepopulateData={selectedIssue as any}
                />
              </div>
              <AddButton
                tooltipTitle={`Add ${selectedTab === 'influencer' ? 'influencer' : 'influenced'}`}
                buttonText={selectedTab === 'influencer' ? 'Influencer' : 'Influenced'}
                onClick={handleAddInfluence}
                className={`w-full ${isSubmitting || !selectedIssue ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              
              {actionMessage && (
                <div className={`text-sm flex items-center ${actionMessage.includes('success') ? 'text-green-600 dark:text-green-400' : 'text-warning-red dark:text-warning-red-dark'}`}>
                  <Info className="mr-1 size-4" />
                  <span>{actionMessage}</span>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default InfluencesModal; 